import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
/* eslint-disable compat/compat */
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

const iterations = 10;
const vtx_shader_source = `#version 300 es
  in vec2 a_vpos;
  in vec2 a_tpos;
  out vec2 v_tpos;

  void main(void) {
    v_tpos = a_tpos;
    gl_Position = vec4(a_vpos, 0.0, 1.0);
  }
`;
const frag_shader_source = `#version 300 es
  precision highp float;
  in vec2 v_tpos;
  uniform vec2 u_size;
  uniform sampler2D u_texture;
  uniform int u_iteration;
  out vec4 outColor;

  void main(void) {
    vec2 direction = u_iteration % 2 == 0
      ? vec2(u_iteration, 0.0)
      : vec2(0.0, u_iteration);

    vec2 off1 = vec2(1.3846153846) * direction;
    vec2 off2 = vec2(3.2307692308) * direction;

    outColor += texture(u_texture, v_tpos) * 0.2270270270;
    outColor += texture(u_texture, v_tpos + (off1 / u_size)) * 0.3162162162;
    outColor += texture(u_texture, v_tpos - (off1 / u_size)) * 0.3162162162;
    outColor += texture(u_texture, v_tpos + (off2 / u_size)) * 0.0702702703;
    outColor += texture(u_texture, v_tpos - (off2 / u_size)) * 0.0702702703;
  }
`;
function createShaderProgram(gl, vertex_shader_source, fragment_shader_source) {
  const vtx_shader = gl.createShader(gl.VERTEX_SHADER);
  const frag_shader = gl.createShader(gl.FRAGMENT_SHADER);
  gl.shaderSource(vtx_shader, vertex_shader_source);
  gl.shaderSource(frag_shader, fragment_shader_source);
  gl.compileShader(vtx_shader);
  gl.compileShader(frag_shader);
  if (!gl.getShaderParameter(vtx_shader, gl.COMPILE_STATUS)) {
    console.error(gl.getShaderInfoLog(vtx_shader));
  }
  if (!gl.getShaderParameter(frag_shader, gl.COMPILE_STATUS)) {
    console.error(gl.getShaderInfoLog(frag_shader));
  }
  const program = gl.createProgram();
  gl.attachShader(program, vtx_shader);
  gl.attachShader(program, frag_shader);
  gl.linkProgram(program);
  return program;
}
function createFramebufferAndTexture(gl, w, h) {
  const buffer = gl.createFramebuffer();
  const texture = gl.createTexture();
  gl.bindFramebuffer(gl.FRAMEBUFFER, buffer);
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
  return [buffer, texture];
}
export class GaussianBlur {
  init() {
    const ctx = new OffscreenCanvas(1, 1).getContext('2d', {
      willReadFrequently: true
    });
    const gl = new OffscreenCanvas(1, 1).getContext('webgl2', {
      willReadFrequently: true
    });
    this.gl = gl;
    this.ctx = ctx;
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    const program = createShaderProgram(gl, vtx_shader_source, frag_shader_source);
    gl.useProgram(program);
    const a_vpos = gl.getAttribLocation(program, 'a_vpos'); // vertex coordinate
    const a_tpos = gl.getAttribLocation(program, 'a_tpos'); // texture coordinate
    const u_size = gl.getUniformLocation(program, 'u_size'); // size of the texture
    const u_iteration = gl.getUniformLocation(program, 'u_iteration'); // current iteration

    this.u_size = u_size;
    this.u_iteration = u_iteration;
    gl.enableVertexAttribArray(a_vpos);
    gl.enableVertexAttribArray(a_tpos);
    const vpos_buf = gl.createBuffer();
    const tpos_buf = gl.createBuffer();
    const idx_buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, vpos_buf);
    // bottom-Left, bottom-Right, top-Right, top-Left
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, 1, 1, -1, 1]), gl.STATIC_DRAW);
    gl.vertexAttribPointer(a_vpos, 2, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ARRAY_BUFFER, tpos_buf);
    // corresponding texture coordinate
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]), gl.STATIC_DRAW);
    gl.vertexAttribPointer(a_tpos, 2, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, idx_buf);
    // we will draw the quad as triangle strip
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array([0, 1, 3, 2]), gl.STATIC_DRAW);
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.texture = texture;
  }
  resize(w, h) {
    const gl = this.gl,
      ctx = this.ctx,
      u_size = this.u_size;
    if (gl.canvas.width === w && gl.canvas.height === h) {
      return;
    }
    gl.canvas.width = w;
    gl.canvas.height = h;
    ctx.canvas.width = w;
    ctx.canvas.height = h;
    gl.viewport(0, 0, w, h);
    gl.uniform2fv(u_size, new Float32Array([w, h]));
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    var _createFramebufferAnd = createFramebufferAndTexture(gl, w, h);
    var _createFramebufferAnd2 = _slicedToArray(_createFramebufferAnd, 2);
    this.buffer1 = _createFramebufferAnd2[0];
    this.texture1 = _createFramebufferAnd2[1];
    var _createFramebufferAnd3 = createFramebufferAndTexture(gl, w, h);
    var _createFramebufferAnd4 = _slicedToArray(_createFramebufferAnd3, 2);
    this.buffer2 = _createFramebufferAnd4[0];
    this.texture2 = _createFramebufferAnd4[1];
  }
  cleanup() {
    const gl = this.gl,
      buffer1 = this.buffer1,
      buffer2 = this.buffer2,
      texture1 = this.texture1,
      texture2 = this.texture2;
    gl.deleteFramebuffer(buffer1);
    gl.deleteFramebuffer(buffer2);
    gl.deleteTexture(texture1);
    gl.deleteTexture(texture2);
    gl.deleteTexture(this.texture);
    gl.deleteProgram(this.program);
    gl.bindTexture(gl.TEXTURE_2D, null);
    this.buffer1 = null;
    this.buffer2 = null;
    this.texture1 = null;
    this.texture2 = null;
    this.texture = null;
    this.gl = null;
    this.ctx = null;
    this.u_size = null;
  }
  draw(videoFrame) {
    const ctx = this.ctx,
      gl = this.gl,
      buffer1 = this.buffer1,
      buffer2 = this.buffer2,
      texture = this.texture,
      texture1 = this.texture1,
      texture2 = this.texture2,
      u_iteration = this.u_iteration;
    ctx.drawImage(videoFrame, 0, 0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, ctx.canvas);
    let b1 = buffer1;
    let b2 = buffer2;
    let t1 = texture1;
    let t2 = texture2;
    for (let i = 0; i < iterations; i++) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, b1);
      gl.bindTexture(gl.TEXTURE_2D, i === 0 ? texture : t2);

      // send current iteration to the shader
      gl.uniform1i(u_iteration, i);

      // draw to the framebuffer
      // the shader will blur the texture
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawElements(gl.TRIANGLE_STRIP, 4, gl.UNSIGNED_SHORT, 0)

      // swap the buffers
      ;
      var _ref = [b1, b2];
      b2 = _ref[0];
      b1 = _ref[1]; // swap the textures
      var _ref2 = [t1, t2];
      t2 = _ref2[0];
      t1 = _ref2[1];
    }

    // draw last blurred texture to the screen
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.bindTexture(gl.TEXTURE_2D, t1);
    gl.drawElements(gl.TRIANGLE_STRIP, 4, gl.UNSIGNED_SHORT, 0);
  }
}